"use client";

import { type RefObject, useEffect, useState } from "react";
import type { UndoHistory } from "./useUndoHistory";

/** Smallest zoom window that will commit, in seconds. */
const MIN_VIEW = 0.2;

/**
 * The bar's zoom window: a `[from, to]` slice of the clip that the bar maps to
 * its full width. Transient view state (not in the edit history). `commitView`
 * applies a new window and pulls the in/out selection into it — an undoable
 * edit — selecting the whole window if the selection falls entirely outside.
 */
export function useZoomView({
  duration,
  selectionRef,
  onChange,
  history,
  minClip = 0.05,
}: {
  duration: number;
  selectionRef: RefObject<{ start: number; end: number }>;
  onChange: (start: number, end: number) => void;
  history: UndoHistory;
  minClip?: number;
}) {
  const [view, setView] = useState({ from: 0, to: duration });
  useEffect(() => {
    setView({ from: 0, to: duration }); // reset the view when a new file loads
  }, [duration]);

  const viewFrom = view.from;
  const viewTo = view.to > view.from ? view.to : duration;
  const viewSpan = Math.max(1e-6, viewTo - viewFrom);
  const zoomed = viewFrom > 0.001 || viewTo < duration - 0.001;
  const resetView = () => setView({ from: 0, to: duration });

  /** Set the zoom window directly (clamped to the clip), without touching in/out. */
  const zoomTo = (from: number, to: number) => {
    const f = Math.max(0, Math.min(from, duration));
    const t = Math.max(f, Math.min(to, duration));
    setView({ from: f, to: t });
  };

  /** Commit a zoom window and pull the in/out selection into it (undoable). */
  const commitView = (a: number, b: number) => {
    const from = Math.min(a, b);
    const to = Math.max(a, b);
    if (to - from < MIN_VIEW) return;
    setView({ from, to });
    const { start: s, end: e } = selectionRef.current;
    let ns = Math.max(from, Math.min(s, to));
    let ne = Math.max(from, Math.min(e, to));
    if (ne - ns < minClip) {
      ns = from;
      ne = to;
    }
    if (ns !== s || ne !== e) {
      history.record();
      onChange(ns, ne);
    }
  };

  return { viewFrom, viewTo, viewSpan, zoomed, resetView, commitView, zoomTo };
}

export type ZoomView = ReturnType<typeof useZoomView>;
